import { useState, useEffect } from "react";
import TweetCard from "@/components/TweetCard";
import { useLatestGeneratedContentQuery } from "@/api/twitter";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Persona } from "@/api/personas";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import { TweetCreationModal } from "@/components/content-creation/TweetCreationModal";
import { DialogTrigger } from "@/components/ui/dialog";
import { CustomRulesModal } from "@/components/modals/CustomRulesModal";

export const tweeterBackendUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_APP_TWEETER_FASTAPI_URL_PRODUCTION
  : import.meta.env.VITE_REACT_APP_TWEETER_FASTAPI_URL_DEVELOPMENT;

function TweetPage({
  personaId,
  personaData,
}: {
  personaId: string;
  personaData: Persona;
}) {
  const queryClient = useQueryClient();
  const {
    isFetching: isContentLoading,
    isSuccess: isContentSuccess,
    data: fetchedTweets,
    error,
    fetchNextPage,
  } = useLatestGeneratedContentQuery(personaId);
  const { toast } = useToast();

  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (error) {
      handleError("Error fetching generated tweets: " + error.message);
    }
  }, [error]);

  const handleError = (message: string) => {
    const container = document.createElement("div");
    container.innerHTML = message; // Set the raw HTML message

    toast({
      description: (
        <div dangerouslySetInnerHTML={{ __html: container.innerHTML }} />
      ),
    });
  };

  useEffect(() => {
    if (!contentContainer || isContentLoading) return;

    function onIntersect(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      });
    }

    const observer = new IntersectionObserver(onIntersect, {
      /* As soon as 1px is visible, we'll trigger the callback. */
      threshold: 0,
    });

    observer.observe(contentContainer);

    return () => observer.disconnect();
  }, [contentContainer, isContentLoading]);

  const invalidateTweets = () =>
    queryClient.invalidateQueries({ queryKey: ["tweets", personaId] });

  return (
    <div className="pt-6">
      {/* Desktop View with Side-by-Side Layout */}
      <div className="">
        <div className="">
          <div className="bg-black border border-gray-800 rounded-lg p-4 md:p-6">
            {renderCreateTweetsContent()}
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to render the Create Tweets content
  function renderCreateTweetsContent() {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-end gap-2">
          <CustomRulesModal
            personaId={personaId ?? ""}
            personaData={personaData}
          >
            <DialogTrigger asChild>
              <Button variant="outline">Edit Agent Guidelines</Button>
            </DialogTrigger>
          </CustomRulesModal>
          <TweetCreationModal persona={personaData}>
            <DialogTrigger asChild>
              <Button className="self-end">Create Content</Button>
            </DialogTrigger>
          </TweetCreationModal>
        </div>

        {isContentSuccess &&
        (fetchedTweets.pages.length === 0 ||
          fetchedTweets.pages[0].data.length === 0) ? (
          <div className="flex justify-center py-4 mt-8">
            <p className="text-center text-muted-foreground">
              No content found.
            </p>
          </div>
        ) : null}
        {/* Fetched Tweets */}
        {fetchedTweets?.pages && fetchedTweets.pages.length > 0 && (
          <div className="w-full">
            <div className="relative grid mt-6 gap-4 grid-cols-1">
              {fetchedTweets?.pages.map((page) =>
                page.data.map((tweet) => (
                  <TweetCard
                    key={tweet.doc_id}
                    tweet={tweet}
                    personaData={personaData}
                    onDelete={invalidateTweets}
                    onRate={invalidateTweets}
                  />
                )),
              )}
              <div className="relative h-6 m-auto">
                <LoaderCircle
                  className="justify-center w-6 h-full animate-spin text-primary"
                  style={{
                    visibility: isContentLoading ? "visible" : "hidden",
                  }}
                />
              </div>
              <div
                ref={setContentContainer}
                className="absolute bottom-0 left-0 right-0 h-[1px]"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default TweetPage;
